/**
 * MasterClassesWidget Component
 *
 * Виджет для управления мастер-классами организации.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Button,
  Box,
  Stack,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
import {
  IconSchool,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconClock,
  IconUsers,
  IconCheck,
  IconX,
  IconCalendarEvent,
  IconClipboardCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import BaseWidget from './BaseWidget';
import MasterClassFormModal from './MasterClassFormModal';
import MasterClassViewModal from './MasterClassViewModal';
import MasterClassResultModal from './MasterClassResultModal';
import MasterClassCancelModal from './MasterClassCancelModal';
import tokens from '../../../theme/designTokens';
import { useSession } from '../../../context/SessionContext';

/**
 * API функции для мастер-классов
 */
const masterClassesApi = {
  async getMasterClasses(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить мастер-классы');
    }
    return response.json();
  },

  async createMasterClass(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать мастер-класс');
    }
    return response.json();
  },

  async updateMasterClass(organizationId, masterClassId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes/${masterClassId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить мастер-класс');
    }
    return response.json();
  },

  async addResult(organizationId, masterClassId, resultComment) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes/${masterClassId}/result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ result_comment: resultComment }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось сохранить результат');
    }
    return response.json();
  },

  async cancelMasterClass(organizationId, masterClassId, reason) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes/${masterClassId}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ cancellation_reason: reason }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось отменить мастер-класс');
    }
    return response.json();
  },

  async deleteMasterClass(organizationId, masterClassId) {
    const response = await fetch(`/api/organizations/${organizationId}/master-classes/${masterClassId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить мастер-класс');
    }
  },

  async getSubjects() {
    const response = await fetch('/api/subjects', {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить предметы');
    }
    return response.json();
  },

  async getTeachers() {
    const response = await fetch('/api/users?role=TEACHER', {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить педагогов');
    }
    return response.json();
  },

  async getUsers() {
    const response = await fetch('/api/users', {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить пользователей');
    }
    return response.json();
  },
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Форматирование даты кратко
 */
const formatDateShort = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получить первый предмет из групп
 */
const getFirstSubjectName = (groups) => {
  if (!groups || groups.length === 0) return 'Без предмета';
  const firstGroup = groups[0];
  return firstGroup.subject_name || 'Без предмета';
};

/**
 * Получить цвет статуса
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'planned':
      return 'yellow';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Получить название статуса
 */
const getStatusLabel = (status) => {
  switch (status) {
    case 'planned':
      return 'Запланировано';
    case 'completed':
      return 'Проведено';
    case 'cancelled':
      return 'Отменено';
    default:
      return status;
  }
};

/**
 * Компонент отображения мастер-класса
 */
const MasterClassItem = ({ masterClass, onView, onEdit, onResult, onCancel, onDelete, isAdmin }) => {
  const subjectName = getFirstSubjectName(masterClass.groups);
  const statusColor = getStatusColor(masterClass.status);
  const statusLabel = getStatusLabel(masterClass.status);
  const isPlanned = masterClass.status === 'planned';
  const now = new Date();
  const scheduledAt = new Date(masterClass.scheduled_at);
  const canAddResult = isPlanned && (isAdmin || scheduledAt <= now);

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
      onClick={() => onView(masterClass)}
    >
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: tokens.colors.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconSchool size={20} color="white" />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {formatDateShort(masterClass.scheduled_at)}
          </Text>
          <Text size="sm" c="dimmed" truncate>
            • {subjectName}
          </Text>
        </Group>

        <Group gap="xs" mt={4}>
          {/* Статус */}
          <Badge
            size="xs"
            color={statusColor}
            variant="filled"
            radius="xl"
            leftSection={
              masterClass.status === 'completed' ? <IconCheck size={10} /> :
              masterClass.status === 'cancelled' ? <IconX size={10} /> : null
            }
          >
            {statusLabel}
          </Badge>

          {/* Перенесено */}
          {masterClass.reschedule_count > 0 && (
            <Badge size="xs" color="orange" variant="light" radius="xl">
              Перенесено {masterClass.reschedule_count}
            </Badge>
          )}

          {/* Изменено */}
          {masterClass.edit_count > 0 && (
            <Badge size="xs" color="blue" variant="light" radius="xl">
              Изменено {masterClass.edit_count}
            </Badge>
          )}
        </Group>

        <Group gap="md" mt={4}>
          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconUsers size={12} />
              {masterClass.total_groups} {masterClass.total_groups === 1 ? 'группа' : 'групп'} • {masterClass.total_children} детей
            </Group>
          </Text>
        </Group>

        {/* Краткий результат если есть */}
        {masterClass.result_comment && (
          <Text size="xs" c="green" mt={4} lineClamp={1}>
            Итог: {masterClass.result_comment}
          </Text>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          {isPlanned && (
            <>
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(masterClass);
                }}
              >
                Редактировать
              </Menu.Item>

              {canAddResult && (
                <Menu.Item
                  leftSection={<IconClipboardCheck size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResult(masterClass);
                  }}
                >
                  Результат МК
                </Menu.Item>
              )}

              <Menu.Item
                leftSection={<IconX size={14} />}
                color="orange"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(masterClass);
                }}
              >
                Отменить МК
              </Menu.Item>
              <Menu.Divider />
            </>
          )}

          {isAdmin && (
            <Menu.Item
              leftSection={<IconTrash size={14} />}
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(masterClass);
              }}
            >
              Удалить
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const MasterClassesWidget = ({ organization, onActivityChange }) => {
  const { user } = useSession();
  const [masterClasses, setMasterClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const canCreate = ['ADMIN', 'MANAGER', 'SALES'].includes(user?.role);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingMasterClass, setEditingMasterClass] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingMasterClass, setViewingMasterClass] = useState(null);

  // Модальное окно результата
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultMasterClass, setResultMasterClass] = useState(null);

  // Модальное окно отмены
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelMasterClass, setCancelMasterClass] = useState(null);

  // Загрузка мастер-классов
  const loadMasterClasses = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await masterClassesApi.getMasterClasses(organization.id);
      setMasterClasses(data || []);
    } catch (err) {
      console.error('Failed to load master classes:', err);
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось загрузить мастер-классы',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  // Загрузка справочников
  const loadReferenceData = useCallback(async () => {
    try {
      const [subjectsData, usersData] = await Promise.all([
        masterClassesApi.getSubjects(),
        masterClassesApi.getUsers(),
      ]);
      setSubjects(subjectsData || []);
      setUsers(usersData.items || []);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  }, []);

  useEffect(() => {
    loadMasterClasses();
    loadReferenceData();
  }, [loadMasterClasses, loadReferenceData]);

  // Открыть модальное окно для добавления
  const handleAdd = () => {
    setEditingMasterClass(null);
    setFormModalOpen(true);
  };

  // Открыть модальное окно для просмотра
  const handleView = (mc) => {
    setViewingMasterClass(mc);
    setViewModalOpen(true);
  };

  // Открыть модальное окно для редактирования
  const handleEdit = (mc) => {
    setViewModalOpen(false);
    setViewingMasterClass(null);
    setEditingMasterClass(mc);
    setFormModalOpen(true);
  };

  // Открыть модальное окно для результата
  const handleResult = (mc) => {
    setViewModalOpen(false);
    setViewingMasterClass(null);
    setResultMasterClass(mc);
    setResultModalOpen(true);
  };

  // Открыть модальное окно для отмены
  const handleCancel = (mc) => {
    setViewModalOpen(false);
    setViewingMasterClass(null);
    setCancelMasterClass(mc);
    setCancelModalOpen(true);
  };

  // Удалить мастер-класс
  const handleDelete = async (mc) => {
    if (!window.confirm(`Удалить мастер-класс от ${formatDateShort(mc.scheduled_at)}?`)) {
      return;
    }

    try {
      await masterClassesApi.deleteMasterClass(organization.id, mc.id);
      await loadMasterClasses();
      if (onActivityChange) {
        onActivityChange();
      }
      notifications.show({
        title: 'Успешно',
        message: 'Мастер-класс удалён',
        color: 'green',
      });
    } catch (err) {
      console.error('Failed to delete master class:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    }
  };

  // Сохранить мастер-класс (создание или обновление)
  const handleSubmit = async (data) => {
    setActionLoading(true);
    try {
      if (editingMasterClass) {
        await masterClassesApi.updateMasterClass(organization.id, editingMasterClass.id, data);
        notifications.show({
          title: 'Успешно',
          message: 'Мастер-класс обновлён',
          color: 'green',
        });
      } else {
        await masterClassesApi.createMasterClass(organization.id, data);
        notifications.show({
          title: 'Успешно',
          message: 'Мастер-класс создан',
          color: 'green',
        });
      }
      setFormModalOpen(false);
      setEditingMasterClass(null);
      await loadMasterClasses();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to save master class:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Сохранить результат
  const handleSaveResult = async (resultComment) => {
    if (!resultMasterClass) return;

    setActionLoading(true);
    try {
      await masterClassesApi.addResult(organization.id, resultMasterClass.id, resultComment);
      setResultModalOpen(false);
      setResultMasterClass(null);
      await loadMasterClasses();
      if (onActivityChange) {
        onActivityChange();
      }
      notifications.show({
        title: 'Успешно',
        message: 'Мастер-класс проведён',
        color: 'green',
      });
    } catch (err) {
      console.error('Failed to save result:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Отменить мастер-класс
  const handleConfirmCancel = async (reason) => {
    if (!cancelMasterClass) return;

    setActionLoading(true);
    try {
      await masterClassesApi.cancelMasterClass(organization.id, cancelMasterClass.id, reason);
      setCancelModalOpen(false);
      setCancelMasterClass(null);
      await loadMasterClasses();
      if (onActivityChange) {
        onActivityChange();
      }
      notifications.show({
        title: 'Мастер-класс отменён',
        message: reason ? `Причина: ${reason}` : 'Мастер-класс был отменён',
        color: 'orange',
      });
    } catch (err) {
      console.error('Failed to cancel master class:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <BaseWidget
        title="Мастер-классы"
        icon={IconSchool}
        count={masterClasses.length}
        collapsible={true}
        defaultExpanded={masterClasses.length > 0}
        actions={
          canCreate && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                handleAdd();
              }}
            >
              Добавить
            </Button>
          )
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : masterClasses.length > 0 ? (
          <Stack gap="xs">
            {masterClasses.map((mc) => (
              <MasterClassItem
                key={mc.id}
                masterClass={mc}
                onView={handleView}
                onEdit={handleEdit}
                onResult={handleResult}
                onCancel={handleCancel}
                onDelete={handleDelete}
                isAdmin={isAdmin}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconSchool
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Нет запланированных мастер-классов
            </Text>
            {canCreate && (
              <Button
                variant="light"
                size="xs"
                mt="md"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
              >
                Запланировать мастер-класс
              </Button>
            )}
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы */}
      <MasterClassFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingMasterClass(null);
        }}
        onSubmit={handleSubmit}
        masterClass={editingMasterClass}
        organization={organization}
        subjects={subjects}
        users={users}
        loading={actionLoading}
      />

      {/* Модальное окно просмотра */}
      <MasterClassViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingMasterClass(null);
        }}
        masterClass={viewingMasterClass}
        onEdit={handleEdit}
        onResult={handleResult}
        onCancel={handleCancel}
        isAdmin={isAdmin}
      />

      {/* Модальное окно результата */}
      <MasterClassResultModal
        opened={resultModalOpen}
        onClose={() => {
          setResultModalOpen(false);
          setResultMasterClass(null);
        }}
        onConfirm={handleSaveResult}
        masterClass={resultMasterClass}
        loading={actionLoading}
      />

      {/* Модальное окно отмены */}
      <MasterClassCancelModal
        opened={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelMasterClass(null);
        }}
        onConfirm={handleConfirmCancel}
        masterClass={cancelMasterClass}
        loading={actionLoading}
      />
    </>
  );
};

export default MasterClassesWidget;
